<template>
  <div class="flex flex-row items-center">
    <BaseMemberAvatar :member="member" class="mr-4" />
    <div class="flex flex-col">
      <span class="text-body-large font-medium">{{ memberFullName(member) }}</span>
      <span v-if="secondaryLine" class="text-body-medium text-on-surface-variant">{{
        secondaryLine
      }}</span>
    </div>
    <div class="ml-auto flex flex-row items-center gap-1">
      <BaseChip v-for="role in member.roles" :key="role">{{
        $t(`organization.roles.${role}`)
      }}</BaseChip>
    </div>
    <BaseMenu :items="menuItems" />
    <BaseDialog
      type="basic"
      :is-open="isOpenEditRolesDialog"
      :title="$t('organization.editRoles', { member: memberFullName(member) })"
      class="md:min-w-110"
      @close="closeEditRolesDialog"
    >
      <OrganizationRoleSelect v-model="selectedRoles" :available-roles="availableRoles" />
      <template #dismiss>
        <BaseTextButton @click="closeEditRolesDialog" size="M">{{
          $t('common.cancel')
        }}</BaseTextButton></template
      >
      <template #confirm>
        <BaseTextButton @click="editRoles" size="M">{{ $t('common.save') }}</BaseTextButton>
      </template>
    </BaseDialog>
    <BaseDialog
      type="basic"
      :is-open="isOpenEditNameDialog"
      :title="$t('organization.loginLessMember.editName')"
      class="md:w-110"
      @close="closeEditNameDialog"
    >
      <div class="flex flex-col gap-2">
        <BaseInputField
          v-model="editedFirstName"
          type="text"
          required
          :label="$t('organization.loginLessMember.firstName')"
          :error="showFirstNameError ? 'organization.loginLessMember.firstNameError' : ''"
        />
        <BaseInputField
          v-model="editedLastName"
          type="text"
          :label="$t('organization.loginLessMember.lastName')"
        />
      </div>
      <template #dismiss>
        <BaseTextButton @click="closeEditNameDialog">{{ $t('common.cancel') }}</BaseTextButton>
      </template>
      <template #confirm>
        <BaseTextButton @click="saveName">{{ $t('common.save') }}</BaseTextButton>
      </template>
    </BaseDialog>
    <MemberAccessCodeDialog
      v-if="!member.canLogin"
      :is-open="isOpenAccessCodeDialog"
      :member-id="member.id"
      :member-name="memberFullName(member)"
      @close="closeAccessCodeDialog"
    />
    <BaseDialog
      type="basic"
      :is-open="isOpenDeleteMemberDialog"
      :title="
        $t('organization.settings.deleteMember', {
          member: memberFullName(member),
        })
      "
      class="md:w-100"
      :description="
        $t('organization.settings.deleteMemberDescription', {
          member: memberFullName(member),
        })
      "
      @close="closeDeleteMemberDialog"
    >
      <template #dismiss>
        <BaseTextButton @click="closeDeleteMemberDialog">{{ $t('common.cancel') }}</BaseTextButton>
      </template>
      <template #confirm>
        <BaseTextButton @click="deleteMember">{{ $t('common.delete') }}</BaseTextButton>
      </template>
    </BaseDialog>
  </div>
</template>
<script lang="ts" setup>
  import type { ManageableMember } from '../composables/useOrganization'
  import BaseMemberAvatar from '../../../core/components/base/avatar/BaseMemberAvatar.vue'
  import BaseChip from '../../../core/components/base/chip/BaseChip.vue'
  import BaseMenu from '../../../core/components/base/menu/BaseMenu.vue'
  import { computed, ref, watch } from 'vue'
  import { type Role } from '../../organization/models/organization-role'
  import BaseDialog from '../../../core/components/base/dialog/BaseDialog.vue'
  import BaseTextButton from '../../../core/components/base/button/text-button/BaseTextButton.vue'
  import { useDialog } from '../../../core/components/base/dialog/use-dialog'
  import OrganizationRoleSelect from './OrganizationRoleSelect.vue'
  import { memberFullName } from '@/core/models/member/member-name'
  import type { MenuItemType, SingleMenuItem } from '@/core/components/base/menu/MenuItem.ts'
  import BaseInputField from '@/core/components/base/form-fields/BaseInputField.vue'
  import MemberAccessCodeDialog from '@/modules/lumia/components/MemberAccessCodeDialog.vue'
  import { useI18n } from 'vue-i18n'
  import type { LoginLessMemberName } from '../composables/useOrganizationSettings'

  interface Props {
    member: ManageableMember
    availableRoles: ReadonlyArray<Role>
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    (e: 'editedRoles', roles: Role[]): void
    (e: 'nameUpdated', name: LoginLessMemberName): void
    (e: 'deleted'): void
  }>()

  const { t } = useI18n()

  const {
    isOpen: isOpenEditRolesDialog,
    openDialog: openEditRolesDialog,
    closeDialog: closeEditRolesDialog,
  } = useDialog()

  const {
    isOpen: isOpenDeleteMemberDialog,
    openDialog: openDeleteMemberDialog,
    closeDialog: closeDeleteMemberDialog,
  } = useDialog()

  const {
    isOpen: isOpenEditNameDialog,
    openDialog: openEditNameDialog,
    closeDialog: closeEditNameDialog,
  } = useDialog()

  const {
    isOpen: isOpenAccessCodeDialog,
    openDialog: openAccessCodeDialog,
    closeDialog: closeAccessCodeDialog,
  } = useDialog()

  const selectedRoles = ref<Role[]>([...props.member.roles])
  const editedFirstName = ref<string>()
  const editedLastName = ref<string>()
  const showFirstNameError = ref(false)

  watch(editedFirstName, () => {
    if (editedFirstName.value?.trim()) {
      showFirstNameError.value = false
    }
  })

  const secondaryLine = computed(() => {
    const lines: string[] = []
    if (props.member.email) {
      if (props.member.pending) {
        lines.push(t('common.pending'))
      } else {
        lines.push(props.member.email)
      }
    }
    if (props.member.teams.length > 0) {
      lines.push(...props.member.teams.map((team) => team.name))
    }
    return lines.join(' ・ ')
  })

  const openEditRoles = () => {
    selectedRoles.value = [...props.member.roles]
    openEditRolesDialog()
  }

  const openEditName = () => {
    editedFirstName.value = props.member.firstName
    editedLastName.value = props.member.lastName
    showFirstNameError.value = false
    openEditNameDialog()
  }

  const menuItems = computed<MenuItemType>(() => {
    const items: SingleMenuItem[] = []

    if (props.member.canLogin) {
      items.push({
        id: 'edit',
        title: 'Rollen anpassen',
        leadingIcon: 'edit',
        callback: openEditRoles,
      })
    } else {
      items.push({
        id: 'edit-name',
        title: t('organization.loginLessMember.editName'),
        leadingIcon: 'edit',
        callback: openEditName,
      })
      items.push({
        id: 'access-code',
        title: t('organization.loginLessMember.accessCode.show'),
        leadingIcon: 'password',
        callback: openAccessCodeDialog,
      })
    }

    return [
      { items: items },
      {
        items: [
          {
            id: 'remove',
            title: 'Entfernen',
            leadingIcon: 'delete',
            callback: openDeleteMemberDialog,
            destructive: true,
          },
        ],
      },
    ]
  })

  const saveName = () => {
    const firstName = editedFirstName.value?.trim() ?? ''
    if (!firstName) {
      showFirstNameError.value = true
      return
    }
    const lastName = editedLastName.value?.trim()
    emit('nameUpdated', lastName ? { firstName, lastName } : { firstName })
    closeEditNameDialog()
  }

  const editRoles = () => {
    emit('editedRoles', selectedRoles.value)
    closeEditRolesDialog()
  }

  const deleteMember = () => {
    emit('deleted')
    closeDeleteMemberDialog()
  }
</script>
